/**
 * Renders the dashboard only when an unexpired JWT is in localStorage.
 *
 * Boot paths:
 *  - `#/auth/verify?session=<jwt>`: the email link was verified by the backend,
 *    which minted the JWT and sent us here. Hydrate user + org, store, strip.
 *  - `#/auth/denied?reason=…`: the backend refused the link (no account, no
 *    dashboard access, or an expired link). Show the sign-in screen with the
 *    reason so the person knows what to do next.
 *  - `#/auth/verify?token=…`: the lab paste-token path (only when the backend
 *    has demo auth on).
 *  - A valid JWT in storage: straight in.
 *  - Nothing: sign-in.
 *
 * Mock mode never talks to a backend and skips the gate.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { MOCK_MODE, apiHost } from '../api';
import {
  clearAuth,
  hydrateAuthFromJwt,
  loadAuth,
  parseDeniedFromHash,
  parseSessionFromHash,
  parseTokenFromHash,
  storeAuth,
  verifyMagicLink,
  type AuthPayload,
  type DeniedReason,
} from './auth';
import { SignIn } from './SignIn';

interface Props {
  children: ReactNode;
}

type GateState =
  | { kind: 'mock' }
  | { kind: 'verifying-token'; token: string }
  | { kind: 'hydrating-session'; jwt: string }
  | { kind: 'authed'; auth: AuthPayload }
  | { kind: 'anon'; error?: string; denied?: DeniedReason };

export function AuthGate({ children }: Props) {
  const [state, setState] = useState<GateState>(() => (MOCK_MODE ? { kind: 'mock' } : initial()));

  useEffect(() => {
    if (state.kind === 'verifying-token') {
      let cancelled = false;
      verifyMagicLink(apiHost, state.token)
        .then((payload) => {
          if (cancelled) return;
          storeAuth(payload);
          stripHash();
          setState({ kind: 'authed', auth: payload });
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          clearAuth();
          setState({ kind: 'anon', error: err instanceof Error ? err.message : String(err) });
        });
      return () => {
        cancelled = true;
      };
    }
    if (state.kind === 'hydrating-session') {
      let cancelled = false;
      hydrateAuthFromJwt(apiHost, state.jwt)
        .then((payload) => {
          if (cancelled) return;
          storeAuth(payload);
          stripHash();
          setState({ kind: 'authed', auth: payload });
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          clearAuth();
          setState({ kind: 'anon', error: err instanceof Error ? err.message : String(err) });
        });
      return () => {
        cancelled = true;
      };
    }
    return;
  }, [
    state.kind,
    state.kind === 'verifying-token' ? state.token : '',
    state.kind === 'hydrating-session' ? state.jwt : '',
  ]);

  // A sign-in hash can also arrive without a page load (a link opened into an
  // already-open tab, or the denied redirect landing on a tab that had the
  // dashboard open). Re-read the hash when it changes to an auth route.
  useEffect(() => {
    if (MOCK_MODE) return;
    const onHash = () => {
      if (!window.location.hash.startsWith('#/auth/')) return;
      setState(initial());
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  if (state.kind === 'mock' || state.kind === 'authed') return <>{children}</>;
  if (state.kind === 'verifying-token' || state.kind === 'hydrating-session') {
    return (
      <div className="auth">
        <div className="t-secondary">
          <span className="spinner" /> Signing you in…
        </div>
      </div>
    );
  }
  return (
    <SignIn
      error={state.error}
      denied={state.denied}
      onSignedIn={() => {
        const auth = loadAuth();
        if (auth) setState({ kind: 'authed', auth });
      }}
    />
  );
}

function stripHash(): void {
  if (typeof window === 'undefined') return;
  const url = window.location.pathname + window.location.search;
  window.history.replaceState(null, '', url || '/');
}

function initial(): GateState {
  if (typeof window === 'undefined') {
    const existing = loadAuth();
    return existing ? { kind: 'authed', auth: existing } : { kind: 'anon' };
  }
  const session = parseSessionFromHash(window.location.hash);
  if (session) return { kind: 'hydrating-session', jwt: session };
  const token = parseTokenFromHash(window.location.hash);
  if (token) return { kind: 'verifying-token', token };
  const denied = parseDeniedFromHash(window.location.hash);
  if (denied) {
    stripHash();
    return { kind: 'anon', denied };
  }
  const existing = loadAuth();
  if (existing) return { kind: 'authed', auth: existing };
  return { kind: 'anon' };
}
